import { useChat } from "@/contexts/ChatContext";
import { cn } from "@/lib/utils";
import PropTypes from "prop-types";
import { memo, useCallback } from "react";
import {
  DeepResearchBlock,
  MessageActions,
  MessageAvatar,
  MessageContent,
  MessageErrorBoundary,
  ReasoningBlock,
  SourcesAvatars,
} from "./components";
import { useMessageState } from "./hooks/useMessageState";

/**
 * DeepResearchMessage - Specialized message component for deep research queries
 * Handles deep research metadata flags and custom rendering paths
 *
 * @component
 * @example
 * <DeepResearchMessage
 *   message={deepResearchMessageObject}
 *   isStreaming={false}
 *   onEdit={handleEdit}
 * />
 */
const DeepResearchMessage = memo(function DeepResearchMessage({
  message,
  isStreaming: isStreamingProp = false,
  onEdit = null,
  className = "",
}) {
  // Consolidate message state
  const {
    effectiveMessage,
    effectiveIsStreaming,
    hasToolCalls,
    hasCompletedToolCalls,
    toolCalls: _toolCalls,
    previousUserMessage: _previousUserMessage,
    previousAssistantMessage,
    effectiveWebSearchData,
    effectiveDeepResearchData,
    isDeepResearchMessage,
  } = useMessageState({
    message,
    isStreaming: isStreamingProp,
    allMessages: [],
  });

  const isUser = effectiveMessage?.role === "user";
  const content = effectiveMessage?.content || "";

  // Get chat context for abort functionality, reply, and sources sidebar
  const chatContext = useChat();
  const {
    startReplyToMessage,
    sourcesSidebarOpen,
    setSourcesSidebarOpen,
    setActiveSources,
    setDeepResearchSidebarOpen,
    setImageArtifactSidebarOpen,
    handleAbortStream,
  } = chatContext || {};

  // Handler for copy action
  const copyToClipboard = useCallback(async () => {
    const textToCopy = content || "";
    try {
      await navigator.clipboard.writeText(textToCopy);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  }, [content]);

  // Reply handler
  const handleReply = useCallback(() => {
    if (startReplyToMessage && effectiveMessage) {
      startReplyToMessage(effectiveMessage);
    }
  }, [startReplyToMessage, effectiveMessage]);

  // Sources click handler
  const handleSourcesClick = useCallback(
    (sources) => {
      // If sources sidebar is already open with the same sources, close it
      if (sourcesSidebarOpen) {
        setSourcesSidebarOpen(false);
        setActiveSources(null);
        return;
      }

      // Close all other sidebars before opening sources sidebar
      setDeepResearchSidebarOpen(false);
      setImageArtifactSidebarOpen(false);

      // Set sources and open sources sidebar
      setActiveSources(sources);
      setSourcesSidebarOpen(true);
    },
    [
      sourcesSidebarOpen,
      setSourcesSidebarOpen,
      setActiveSources,
      setDeepResearchSidebarOpen,
      setImageArtifactSidebarOpen,
    ]
  );

  // Deep research specific metadata
  const isDeepResearchFinalReport =
    effectiveMessage?.metadata?.isDeepResearchFinalReport;
  const deepResearchThreadId = effectiveMessage?.metadata?.deepResearchThreadId;

  // Reasoning block state
  const reasoningContent =
    effectiveMessage?.metadata?.reasoningContent ||
    effectiveMessage?.reasoningContent ||
    effectiveMessage?.metadata?.reasoning_content;

  const isReasoningResponse = effectiveMessage?.metadata?.isReasoningResponse;

  // User messages have different layout (right-aligned bubble)
  if (isUser) {
    return (
      <MessageErrorBoundary messageId={effectiveMessage?.id}>
        <div
          className={cn("group relative w-full", "user-message", className)}
          data-message-id={effectiveMessage?.id}
          data-role={effectiveMessage?.role}
        >
          {/* User message wrapper for right alignment */}
          <div className="flex justify-end w-full pr-6 sm:pr-12">
            <div className="flex flex-col items-end max-w-[75%]">
              {/* User message bubble */}
              <div className="bg-muted/50 px-3 py-1 sm:px-4 sm:py-1.5 md:px-4 md:py-2 rounded-3xl">
                <MessageContent
                  displayContent={content}
                  effectiveIsStreaming={effectiveIsStreaming}
                  handleCitationClick={() => {}}
                  isUser={isUser}
                  isEditing={false}
                  editContent=""
                  editTextareaRef={null}
                  handleInputChange={() => {}}
                  handleEditSubmit={() => {}}
                  handleCancelEdit={() => {}}
                  sources={effectiveWebSearchData}
                  message={effectiveMessage}
                />
              </div>

              {/* Sources Avatars - above action buttons */}
              {effectiveWebSearchData && (
                <SourcesAvatars
                  sources={effectiveWebSearchData}
                  className="mt-2"
                />
              )}

              {/* User message actions below bubble */}
              <MessageActions
                isUser={isUser}
                message={effectiveMessage}
                content={content}
                displayContent={content}
                isStreaming={effectiveIsStreaming}
                showButtons={!effectiveIsStreaming || hasCompletedToolCalls}
                hasToolCalls={hasToolCalls}
                hasCompletedToolCalls={hasCompletedToolCalls}
                hasFileAttachments={false}
                toolData={{
                  firecrawlSearchData: effectiveWebSearchData,
                  dalleImageData: null,
                  canvasData: null,
                  previousMessageHasDeepResearch: isDeepResearchMessage,
                  previousMessageFirecrawlData: null,
                  previousMessageImageData: null,
                  previousMessageCanvasData: null,
                }}
                onCopy={copyToClipboard}
                onEdit={onEdit}
                onShare={() => {}}
                onRerun={() => {}}
                onReply={handleReply}
                onSourcesClick={handleSourcesClick}
                onCanvasClick={() => {}}
                shareButtonEnabled={false}
              />
            </div>
          </div>
        </div>
      </MessageErrorBoundary>
    );
  }

  // Assistant messages use grid layout with avatar
  return (
    <MessageErrorBoundary messageId={effectiveMessage?.id}>
      <div
        className={cn(
          "group relative w-full",
          "assistant-message",
          "deep-research-message",
          "mt-6",
          className
        )}
        data-message-id={effectiveMessage?.id}
        data-role={effectiveMessage?.role}
        data-deep-research={isDeepResearchMessage}
      >
        {/* Grid layout: avatar column + content column */}
        <div className="grid grid-cols-[auto_1fr] gap-3 sm:gap-4 md:gap-6">
          {/* Avatar Column */}
          <MessageAvatar
            isUser={isUser}
            message={effectiveMessage}
            previousMessage={previousAssistantMessage}
            isStreaming={effectiveIsStreaming}
            hasContent={
              Boolean(content && content.trim().length > 0) ||
              Boolean(effectiveDeepResearchData)
            }
            hasToolCalls={hasToolCalls}
            hasCompletedToolCalls={hasCompletedToolCalls}
            isMobile={false}
            isCanvasOpen={false}
          />

          {/* Content Column */}
          <div className="flex flex-col gap-3 min-w-0">
            {/* Reasoning Block (if present) */}
            {isReasoningResponse && reasoningContent && (
              <ReasoningBlock
                content={reasoningContent}
                isStreaming={effectiveIsStreaming}
                messageId={effectiveMessage?.id}
              />
            )}

            {/* Deep Research Progress Block (if research in progress or complete)
                Show even without effectiveDeepResearchData if message is marked as deep research and streaming */}
            {isDeepResearchMessage &&
              (effectiveDeepResearchData || effectiveIsStreaming) && (
                <DeepResearchBlock
                  isStreaming={
                    (effectiveIsStreaming ||
                      effectiveMessage?.metadata?.isDeepResearchInProgress ===
                        true) &&
                    !isDeepResearchFinalReport &&
                    effectiveDeepResearchData?.status !== "complete" &&
                    effectiveDeepResearchData?.status !== "aborted"
                  }
                  isComplete={
                    effectiveDeepResearchData?.status === "complete" ||
                    isDeepResearchFinalReport
                  }
                  isAborted={effectiveDeepResearchData?.status === "aborted"}
                  activity={effectiveDeepResearchData?.activity || []}
                  sources={effectiveDeepResearchData?.sources || []}
                  progressPercentage={
                    effectiveDeepResearchData?.progressPercentage || 0
                  }
                  elapsedTime={effectiveDeepResearchData?.elapsedTime || 0}
                  activitiesCount={
                    effectiveDeepResearchData?.activitiesCount ||
                    effectiveDeepResearchData?.activity?.length ||
                    0
                  }
                  sourcesCount={
                    effectiveDeepResearchData?.sourcesCount ||
                    effectiveDeepResearchData?.sources?.length ||
                    0
                  }
                  onAbort={handleAbortStream}
                  messageId={effectiveMessage?.id}
                />
              )}

            {/* Main Message Content */}
            {content && (
              <MessageContent
                displayContent={content}
                effectiveIsStreaming={effectiveIsStreaming}
                handleCitationClick={() => {}}
                isUser={isUser}
                isEditing={false}
                editContent=""
                editTextareaRef={null}
                handleInputChange={() => {}}
                handleEditSubmit={() => {}}
                handleCancelEdit={() => {}}
                sources={effectiveWebSearchData}
                message={effectiveMessage}
              />
            )}

            {/* Sources Avatars - above action buttons */}
            {effectiveWebSearchData && (
              <SourcesAvatars
                sources={effectiveWebSearchData}
                className="mt-2"
              />
            )}

            {/* Message Actions */}
            <MessageActions
              isUser={isUser}
              message={effectiveMessage}
              content={content}
              displayContent={content}
              isStreaming={effectiveIsStreaming}
              showButtons={!effectiveIsStreaming || hasCompletedToolCalls}
              hasToolCalls={hasToolCalls}
              hasCompletedToolCalls={hasCompletedToolCalls}
              hasFileAttachments={false}
              toolData={{
                firecrawlSearchData: effectiveWebSearchData,
                dalleImageData: null,
                canvasData: null,
                previousMessageHasDeepResearch: isDeepResearchMessage,
                previousMessageFirecrawlData: null,
                previousMessageImageData: null,
                previousMessageCanvasData: null,
              }}
              onCopy={copyToClipboard}
              onEdit={onEdit}
              onShare={() => {}}
              onRerun={() => {}}
              onReply={handleReply}
              onSourcesClick={handleSourcesClick}
              onCanvasClick={() => {}}
              shareButtonEnabled={false}
            />
          </div>
        </div>
      </div>
    </MessageErrorBoundary>
  );
});

DeepResearchMessage.propTypes = {
  /** Deep research message object with metadata flags */
  message: PropTypes.shape({
    id: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    content: PropTypes.string,
    metadata: PropTypes.shape({
      isDeepResearch: PropTypes.bool,
      isDeepResearchFinalReport: PropTypes.bool,
      deepResearchThreadId: PropTypes.string,
      reasoningContent: PropTypes.string,
      isReasoningResponse: PropTypes.bool,
    }),
    tool_calls: PropTypes.array,
    reasoningContent: PropTypes.string,
  }).isRequired,

  /** Whether the message is currently streaming */
  isStreaming: PropTypes.bool,

  /** Callback when edit mode is triggered */
  onEdit: PropTypes.func,

  /** Additional CSS classes */
  className: PropTypes.string,
};

export default DeepResearchMessage;
